import { Roles } from '@common/types/user';
import { CompanySetupContext } from '@company/contexts/CompanySetupContext';
import { DetailsStep } from '@company/routes/public/Setup/Steps/DetailsStep';
import { InformationStep } from '@company/routes/public/Setup/Steps/InformationStep';
import { SetupStep } from '@company/routes/public/Setup/Steps/SetupStep';
import { CompanyCreationSchema } from '@company/schemas/CompanySchema';
import { useCreateManagementCompanyMutation, useRequestNewCompanyMutation } from '@company/services/company';
import { ICompanySchema } from '@company/types/company';
import { companySetupSteps } from '@company/utils/company';
import { PublicLayoutContent } from '@components/layout/public';
import { useAppDispatch, useAppSelector } from '@hooks/generic';
import useExtensions from '@hooks/useExtensions';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import { Button, CircularProgress, Step, StepButton, StepLabel, Stepper } from '@mui/material';
import { setTitle } from '@store/appSlice';
import { selectUser } from '@store/userSlice';
import { useEditor } from '@tiptap/react';
import { capitalizeFirstLetter, handleError, toaster } from '@utils/common';
import { FormikTouched, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';

const CompanySetup = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [isValidatingVatChamber, setIsValidatingVatChamber] = useState(false);
    const disptach = useAppDispatch();
    const user = useAppSelector(selectUser);
    const dashboard = useMemo(() => window.location.pathname.toLowerCase().includes('/dashboard/'), []);
    const [createManagementCompany, { isLoading: isCreateManagingCompanyLoading }] =
        useCreateManagementCompanyMutation();
    const [requestNewCompany, { isLoading: isRequestNewCompanyLoading }] = useRequestNewCompanyMutation();
    const isCreateLoading = useMemo(
        () => isCreateManagingCompanyLoading || isRequestNewCompanyLoading,
        [isCreateManagingCompanyLoading, isRequestNewCompanyLoading]
    );
    const navigate = usePrefixedNavigate();

    useEffect(() => {
        disptach(setTitle('Company setup'));
    }, [disptach]);

    const formik = useFormik<Partial<ICompanySchema>>({
        initialValues: {
            isAdmin: dashboard && user?.role === Roles.VICTRON,
        },
        validationSchema: CompanyCreationSchema,
        onSubmit: async (values) => {
            try {
                if (dashboard) {
                    await createManagementCompany(values).unwrap();
                } else {
                    await requestNewCompany({
                        ...values,
                        company_name: values.name,
                        services_ids: values.services?.map((service) => service.id),
                    }).unwrap();
                }
                toaster.success({ msg: `${values.name} was successfully created` });
                navigate(`${dashboard ? '/dashboard' : ''}/companies/`);
            } catch (err) {
                handleError('Error creating company', err);
            }
        },
    });

    const extensions = useExtensions();
    const editor = useEditor({
        extensions: extensions,
        onUpdate({ editor }) {
            const value = editor.getHTML();
            formik.handleChange('description')(value);
        },
    });

    const handleBack = useCallback(() => {
        setActiveStep((prevStep) => prevStep - 1);
    }, []);

    const validateVatChamberFields = useCallback(async () => {
        try {
            setIsValidatingVatChamber(true);
            if (dashboard) {
                await createManagementCompany({
                    vat_number: formik.values.vat_number,
                    chamber_of_commerce_number: formik.values.chamber_of_commerce_number,
                    commit: false,
                }).unwrap();
            } else {
                await requestNewCompany({
                    vat_number: formik.values.vat_number,
                    chamber_of_commerce_number: formik.values.chamber_of_commerce_number,
                    commit: false,
                }).unwrap();
            }
        } catch (err) {
            throw err;
        } finally {
            setIsValidatingVatChamber(false);
        }
    }, [
        createManagementCompany,
        dashboard,
        formik.values.chamber_of_commerce_number,
        formik.values.vat_number,
        requestNewCompany,
    ]);

    const stepErrors = useMemo(
        () =>
            [0, 1, 2].reduce((acc, step) => {
                acc[step] = companySetupSteps[step].fields.some(
                    (field) =>
                        formik.touched[field as keyof ICompanySchema] && formik.errors[field as keyof ICompanySchema]
                );
                return acc;
            }, {} as Record<number, boolean>),
        [formik.touched, formik.errors]
    );

    const hasPreviousErrors = useCallback(
        (step: number) =>
            Object.entries(stepErrors)
                .filter(([key]) => +key < step)
                .some(([, value]) => !!value),
        [stepErrors]
    );

    const validateStep = useCallback(async () => {
        const touchedFields = companySetupSteps[activeStep].fields.reduce((acc, field) => {
            acc[field as keyof ICompanySchema] = true;
            return acc;
        }, {} as FormikTouched<Partial<ICompanySchema>>);
        formik.setTouched(touchedFields);

        const errors = await formik.validateForm();
        const currentStepErrors = companySetupSteps[activeStep].fields.some(
            (field) => errors[field as keyof ICompanySchema]
        );

        if (currentStepErrors) {
            throw false;
        }

        if (activeStep === 0) {
            await validateVatChamberFields().catch((err) => {
                formik.setErrors({
                    ...formik.errors,
                    ...Object.fromEntries(
                        Object.entries(err.data as Record<string, string[]>).map(([key, value]) => [
                            key,
                            capitalizeFirstLetter(value[0]),
                        ])
                    ),
                });
                throw false;
            });
        }
    }, [activeStep, formik, validateVatChamberFields]);

    const handleStepChange = useCallback(
        async (step: number) => {
            try {
                await validateStep();
                setActiveStep(step);
            } catch (err) {
                const el = document.querySelector('.Mui-error, [data-error]');
                const element = el?.parentElement ?? el;
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                if (step < activeStep) {
                    setActiveStep(step);
                }
            }
        },
        [activeStep, validateStep]
    );

    const step = useMemo(() => {
        switch (activeStep) {
            default:
            case 0:
                return <SetupStep />;
            case 1:
                return <InformationStep />;
            case 2:
                return <DetailsStep />;
        }
    }, [activeStep]);

    const Setup = (
        <CompanySetupContext.Provider value={{ formik, editor }}>
            <div className="md:px-16 py-11 h-full">
                <div className="bg-white flex flex-col h-full p-8 rounded">
                    <p className="font-bold text-3xl lg:text-5xl px-2 md:px-0 mb-5">Company setup</p>
                    <p className="text-lg font-semibold">{companySetupSteps[activeStep].title}</p>
                    <p>
                        {activeStep === 0
                            ? 'Enter your company details to get started. Provide the company name, describe your services, and select the markets you serve. This information helps set up your company profile in Victron Professional efficiently.'
                            : activeStep === 1
                            ? "Provide your company's contact details, including address, phone number, and email. Accurate contact information ensures seamless communication and enhances your company's profile"
                            : 'Add a detailed description of your company to highlight its mission, values, and unique offerings. This helps create a comprehensive profile that represents your business in Victron Professional.'}
                    </p>
                    <div className="flex flex-col gap-5 py-6 overflow-auto grow">{step}</div>
                    <Stepper nonLinear activeStep={activeStep}>
                        {companySetupSteps.map((step, i) => (
                            <Step key={step.title} disabled={hasPreviousErrors(i)}>
                                <StepButton
                                    className="!py-2"
                                    onClick={() => handleStepChange(i)}
                                    disabled={hasPreviousErrors(i)}
                                    icon={
                                        i === 0 && (formik.isValidating || isValidatingVatChamber) ? (
                                            <CircularProgress size={20} />
                                        ) : undefined
                                    }
                                >
                                    <StepLabel error={stepErrors[i] && activeStep !== i}>{step.title}</StepLabel>
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>
                    <div className="flex pt-4 pb-8">
                        <Button color="inherit" disabled={activeStep === 0} onClick={handleBack}>
                            Back
                        </Button>
                        <div className="flex-auto" />
                        <Button
                            onClick={
                                activeStep !== companySetupSteps.length - 1
                                    ? () => handleStepChange(activeStep + 1)
                                    : formik.submitForm
                            }
                            disabled={isCreateLoading}
                            startIcon={isCreateLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            <span>{activeStep === companySetupSteps.length - 1 ? 'Submit' : 'Next'}</span>
                        </Button>
                    </div>
                </div>
            </div>
        </CompanySetupContext.Provider>
    );

    return (
        <>
            {!dashboard ? (
                <PublicLayoutContent dottedBackgroundProps={{ gradient: true, gradientValue: '10%' }}>{Setup}</PublicLayoutContent>
            ) : (
                Setup
            )}
        </>
    );
};

export { CompanySetup };
